/* eslint-disable clubmed/no-fake-component */
import React, {
  useEffect,
  useCallback,
  useMemo,
  useContext,
  useState,
  useRef,
  useReducer,
} from 'react';
import { get, isEmpty, isNil, omit } from 'lodash';
import { withRouter } from 'next/router';
import URI from 'urijs';
import moment from 'moment';
import { appQueryParameters, url as urlUtils, constants } from '@clubmed/utils';
import type { SelectProposal } from 'Store/booking/selected-proposal-id/selectedProposalId.action';
import graphqlClient from 'Utils/graphql-client/graphqlClient';
import type { StreetViewsByAccommodationCategoryIdQueryParam } from 'Store/streetViews/streetViews.action';
import logger from 'Logger/logger';
import { isStrictlyProductionEnv } from 'Utils/context/contextUtils';
import { urlPattern } from 'Routes/routeBuilder';
import { invariant } from 'Utils/invariant/invariant';
import { getBookingEngineI18n as getI18nAction } from 'Store/cms/cms.actions';
import { ProposalNotFound } from 'Errors/errors';
import { getFromLocalStorage } from 'Client/local-storage-api';
import Cookies from 'js-cookie';
import { HeaderLite } from 'Components/widgets/header-lite/HeaderLite';
import { BookingAccessibleTitle } from 'Components/widgets/booking-accessible-title/BookingAccessibleTitle';
import { Icon } from 'Components/frameworks/icon/Icon';
import { ICONS } from 'Components/utils/icon/icons';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { isFeatureFlipped } from 'Utils/feature-flipping/featureFlippingUtils';
import responsiveness from 'Components/utils/responsive/Responsive';
import { B2CAppContext } from 'Context/ReactAppContext';
import {
  SVRL_PMNTS,
  getAllProposalsIdWithAlternative,
  getDepartureDateRange,
  handleWrongUrl,
  getExclusiveCollectionFromAccomodation,
} from 'Views/booking/BookingEngine.utils';
import { AsyncLoader } from 'Components/widgets/loading/async-loader/AsyncLoader';
import { BookingBreadcrumb } from 'Components/widgets/booking-breadcrumb/BookingBreadcrumb';
import {
  SHOPPING_FAVORITE_PRODUCTS,
  SHOPPING_REMOTE_FAVORITE_PRODUCTS,
  TRACK_EVENT_NAMES,
  TRACK_PAGE_NAMES,
} from 'Tracking/constants';
import { PAYMENT_BOOKING_STATUS } from 'Utils/request/requestDictionary';
import { BookingEngineContext } from 'Views/booking/BookingEngineContext';
import { BookingEngineTrackingContext } from 'Views/booking/BookingEngineTrackingContext';
import { QuickSearchUnavailability } from 'Views/quick-search/QuickSearchUnavailability';
import { BlockMismatchProposalCountry } from 'Views/booking/BlockMismatchProposalCountry';
import { ABTestContext } from 'Context/ABTestContext';
import { BookingFooter } from 'Components/widgets/booking-footer/BookingFooter';
import { classnames } from 'Utils/classnames/classnames.utils';
import { BOOKING_STEPS } from 'Utils/product-comparison/constants';
import { TrackPage } from 'Tracking/track-page/TrackPage';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { dateDiff } from 'Components/widgets/trip-date-picker/TripDatePicker.utils';
import { SaveCriteriaConfirmation } from './save-criteria/SaveCriteriaConfirmation';
import { SaveCriteriaLogin } from './save-criteria/SaveCriteriaLogin';
import { RefreshProposalModal } from './refresh-proposal-modal/RefreshProposalModal';
import ShareContext from './share-context/ShareContext';
import { BookingStep } from './BookingStep';
import { PAGES, BE_SHARE_ACTION, BE_SAVE_ACTION, BE_UNSAVE_ACTION } from './constants';
import { resetTrackingStorageBE } from './BookingEngine.tracking';
import { getFilteredBreadcrumbPages, getStepIndex } from './BookingEngine.utils';
import type { ProposalsByIdQueryParam } from './BookingEngine.query';
import { BookingLayout } from './BookingLayout';
import { ParticipantsReassurance } from './participants/candy-bar/ParticipantsReassurance';
import { TravelPlansComponent } from './travel-plans/TravelPlans';
const { openLayer: SHOW_LAYER_QUERY_NAME } = appQueryParameters;
const { PRODUCT_TYPES } = constants;
type ABTastyData = any;
type Props = {
  getBookingEngineI18n: typeof getI18nAction;
  proposals: Array<Proposal>;
  selectedProposal: Proposal;
  fetchProposalsById: (arg: ProposalsByIdQueryParam) => any;
  fetchRefreshProposal: (
    proposalId: string,
    salesNetworkId: string | null | undefined,
    contextApp: AppContext,
  ) => Promise<void>;
  fetchStreetViewsByAccommodationCategoryId: (
    arg: StreetViewsByAccommodationCategoryIdQueryParam,
  ) => any;
  selectProposal: SelectProposal;
  startLoading: () => unknown;
  endLoading: () => unknown;
  closeToast: () => any;
  displayToastOnContextShare: (toastLabel: string) => any;
  saveCustomerProposalsHandler: (
    proposalId: string,
    contextApp: AppContext,
    customerId: string,
  ) => any;
  patchProposalHandler: (proposalId: string, contextApp: AppContext, extend: boolean) => any;
  i18n: I18N;
  resort: ProductWithAccommodations;
  router: any;
  streetViews: Array<StreetViewWithAccommodationCategoryId>;
  tripInformation: TripInformation;
  responsive: ResponsiveProps;
  leadScore?: number;
  userInfo?: UserInfo;
  trackClickFavorite: (arg0: 'header' | 'bottomBar', arg1: string) => void;
  trackAddToWishlist: () => any;
  trackClickShare: () => any;
  trackClickSkipStep: () => any;
  trackClickPreviousStep: () => any;
  abTastyData: ABTastyData | null | undefined;
};

function shouldFetchDataFromProps(props: Props, _context: any): boolean {
  const { proposals, resort, i18n, router } = props;
  const proposalIds = router.query.proposalIds || [];
  const validProposals = proposals.every(({ id }) => proposalIds.indexOf(id) !== -1);
  const validI18n = ['common', 'filters', 'sorters', 'travelAdvisor', 'booking', 'footer'].every(
    (namespace) => !isNil(get(i18n, namespace)),
  );
  return isEmpty(proposals) || !validProposals || !resort || !validI18n;
}

// Extracted from the original class's getDerivedStateFromProps for testability
export function deriveBookingStepState(props: any, state?: any) {
  if (
    isEmpty(props.proposals) ||
    !props.resort ||
    !props.i18n?.booking ||
    !props.selectedProposal
  ) {
    return null;
  }

  const path = new URI(props.router.asPath).pathname();
  const shouldHideActivitiesStepAbTest = computeShouldHideActivitiesStepAbTest(props.abTastyData);
  const isPartiallyPaid =
    state?.attendeesPaymentStatus?.some((status: any) => status.hasPaid) ?? false;
  const availableSteps = getFilteredBreadcrumbPages({
    selectedProposal: props.selectedProposal,
    resort: props.resort,
    i18n: props.i18n,
    isActivitiesViewFeatureFlipped: state?.displayActivities,
    isOptionHoldOnlyFeatureFlipped: state?.optionHoldOnly,
    isPartiallyPaid,
    shouldHideActivitiesStepAbTest,
  });
  const stepIndex = availableSteps.findIndex((el) => path === el.path);
  const newStep = Math.max(0, stepIndex);
  const newUnlockedStep =
    !state?.previousSelectedProposalId ||
    state.previousSelectedProposalId === props.selectedProposal.id
      ? Math.max(state?.unlockedStep ?? 0, stepIndex)
      : Math.min(
          state?.unlockedStep ?? 0,
          availableSteps.findIndex((el) => el.path.includes('participants')),
        );
  return {
    unlockedStep: newUnlockedStep,
    step: newStep,
    previousSelectedProposalId: props.selectedProposal.id,
  };
}

function computeShouldHideActivitiesStepAbTest(
  abTastyData: ABTastyData | null | undefined,
): boolean {
  return (
    abTastyData?.['ab-test-activities'] === '1' ||
    abTastyData?.['ab-test-activities-br'] === '1' ||
    abTastyData?.['ab-test-activities-ca'] === '1' ||
    abTastyData?.['ab-test-activities-jp'] === '1' ||
    abTastyData?.['ab-test-activities-uk'] === '1'
  );
}

const BookingEngineComponent = React.memo(function BookingEngineComponent(props: Props) {
  const {
    getBookingEngineI18n,
    proposals,
    selectedProposal,
    fetchProposalsById,
    fetchRefreshProposal,
    fetchStreetViewsByAccommodationCategoryId,
    selectProposal,
    startLoading,
    endLoading,
    closeToast,
    displayToastOnContextShare,
    saveCustomerProposalsHandler,
    patchProposalHandler,
    i18n,
    resort,
    router,
    streetViews,
    trackClickFavorite,
    trackAddToWishlist,
    trackClickShare,
    trackClickSkipStep,
    trackClickPreviousStep,
    abTastyData,
  } = props;

  const context = useContext(B2CAppContext);
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);

  const [unlockedStep, setUnlockedStep] = useState(0);
  const [step, setStep] = useState(0);
  const [shouldShowSaveCriteriaConfirmation, setShouldShowSaveCriteriaConfirmation] =
    useState(false);
  const [shouldShowSaveCriteriaLogin, setShouldShowSaveCriteriaLogin] = useState(false);
  const [shouldShowShareContext, setShouldShowShareContext] = useState(false);
  const [_shouldRefreshProposalModal, _setShouldRefreshProposalModal] = useState(false);
  const [persistenceExtended, setPersistenceExtended] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(
    () => !shouldFetchDataFromProps(props, context),
  );
  const [displayActivities] = useState(() =>
    isFeatureFlipped(context)('booking.displayActivities'),
  );
  const [optionHoldOnly, setOptionHoldOnly] = useState(
    () =>
      ((selectedProposal?.households?.length > 1 &&
        isFeatureFlipped(context)('booking.multiHouseholdOptionOnly')) ||
        isFeatureFlipped(context)('booking.optionHoldOnly')) &&
      selectedProposal.price &&
      selectedProposal.price.currency !== 'MXN',
  );
  const [savedProposal, setSavedProposal] = useState<Proposal | null | undefined>(null);
  const [refreshedProposal, setRefreshedProposal] = useState<RefreshedProposal | undefined>(
    undefined,
  );
  const [shouldOpenPanel, setShouldOpenPanel] = useState(false);
  const [isProposalFavorite, setIsProposalFavorite] = useState(false);
  const [isNextStep, setIsNextStep] = useState(false);
  const [displayRefreshProposalError, _setDisplayRefreshProposalError] = useState(false);
  const [hasInternalServerError, setHasInternalServerError] = useState(false);
  const [attendeesPaymentStatus, setAttendeesPaymentStatus] = useState<
    Array<AttendeePaymentStatus>
  >([]);
  const [clickOrigin, setClickOrigin] = useState<'header' | 'bottomBar' | null>(null);

  const previousSelectedProposalIdRef = useRef(selectedProposal.id);
  const _prevPropsRef = useRef(props);
  const _prevStateRef = useRef({
    isDataFetched,
    displayActivities,
    optionHoldOnly,
    unlockedStep,
  });
  const isMountedRef = useRef(false);
  const unlockedStepRef = useRef(unlockedStep);
  unlockedStepRef.current = unlockedStep;
  const stepRef = useRef(step);
  stepRef.current = step;

  function shouldFetchData(): boolean {
    return shouldFetchDataFromProps(props, context);
  }

  function getFilteredPages() {
    const isPartiallyPaid = attendeesPaymentStatus.some((status) => status.hasPaid);
    const shouldHideActivitiesStepAbTest = computeShouldHideActivitiesStepAbTest(abTastyData);
    return getFilteredBreadcrumbPages({
      selectedProposal,
      resort,
      i18n,
      isActivitiesViewFeatureFlipped: displayActivities,
      isOptionHoldOnlyFeatureFlipped: optionHoldOnly,
      isPartiallyPaid,
      shouldHideActivitiesStepAbTest,
    });
  }

  function getCurrentStepIndex(): number {
    const { bookingStep } = router.query;
    return getStepIndex(getFilteredPages(), bookingStep);
  }

  function getCurrentPage(): BookingPageData {
    const { bookingStep } = router.query;
    const currentPage = getFilteredPages().find((page) => page.name === bookingStep);
    invariant(currentPage, `The step ${bookingStep} doesn't exist`);
    return currentPage;
  }

  function isOnLastPage(): boolean {
    return router.query.bookingStep === 'banking';
  }

  function changeURI(pageName: string, proposalId: string | null | undefined, queryParams: any) {
    closeToast();
    const newUrl = new URI(router.asPath)
      .filename(pageName)
      .removeQuery(SHOW_LAYER_QUERY_NAME)
      .removeQuery('catId');
    Object.keys(queryParams).forEach((key) => {
      newUrl.setQuery(key, queryParams[key]);
    });

    if (proposalId) {
      newUrl.setQuery('proposalIds', getAllProposalsIdWithAlternative(selectedProposal));
    }

    return router.push(newUrl.toString());
  }

  function updateStep(
    newStep: number,
    proposalId: string | null | undefined = undefined,
    queryParams: Record<string, string> = {},
  ) {
    const pages = getFilteredPages();
    changeURI(pages[newStep].name, proposalId, queryParams);
    setIsNextStep(true);

    if (typeof window !== 'undefined') {
      setTimeout(() => window.scrollTo(0, 0));
    }
  }

  function handleNoData() {
    if (isDataFetched && (isEmpty(proposals) || !selectedProposal)) {
      logger.error(new ProposalNotFound());

      if (isStrictlyProductionEnv()) {
        const { locale } = router.query;
        return router.replace({
          pathname: '/500',
          query: Boolean(locale) && {
            locale,
          },
        });
      }

      return null;
    }
  }

  async function loadStreetViews({
    resort: resortParam,
    withLoader,
  }: {
    resort: ProductWithAccommodations;
    withLoader: boolean;
  }) {
    const accommodations = resortParam?.accommodations || [];
    const categoryIds =
      accommodations && accommodations.length > 0
        ? [...new Set(accommodations.map(({ categoryId }) => categoryId))]
        : null;

    if (
      resortParam?.id &&
      resortParam.type !== PRODUCT_TYPES.CRUISE &&
      categoryIds &&
      categoryIds.length > 0
    ) {
      if (withLoader) {
        startLoading();
      }

      await fetchStreetViewsByAccommodationCategoryId({
        accommodationCategoryIds: categoryIds,
        appContext: context,
        productId: resortParam.id,
      } as StreetViewsByAccommodationCategoryIdQueryParam & { appContext: AppContext });

      if (withLoader) {
        endLoading();
      }
    }
  }

  async function loadBookingStatus(bookingId?: string) {
    if (!bookingId) {
      return [];
    }

    const response = await graphqlClient.runNoCacheQuery(
      `query {
      sellerBooking (bookingId:"${bookingId}") {
        bookingStatus
        attendees {
          customerId
          hasPaid
        }
      }
    }
    `,
      context,
      {
        requestName: PAYMENT_BOOKING_STATUS,
      },
    );
    return response?.sellerBooking?.attendees || [];
  }

  async function checkIfFavoriFromShopping() {
    const arrivalDateToTimestamp = new Date(
      moment(String(selectedProposal.arrivalDate)).toDate(),
    ).getTime();
    const departureCity =
      selectedProposal.transports?.inbound?.travelSections?.[0]?.departure?.cityId || 'NO';
    const favoriteProductList =
      getFromLocalStorage(
        context.isUserLogged ? SHOPPING_REMOTE_FAVORITE_PRODUCTS : SHOPPING_FAVORITE_PRODUCTS,
      ) || [];
    const isShoppingFavorite = (favoriteProductList as Array<Record<string, unknown>>).some(
      (item) => {
        const favoriteItem = item as {
          adultsNumber?: number;
          departureDate?: number;
          duration?: number;
          departureCity?: string;
          kids?: unknown[];
          product?: { id?: string };
          reference?: string;
        };

        return (
          favoriteItem.adultsNumber === selectedProposal.attendees?.adults?.length &&
          favoriteItem.departureDate === arrivalDateToTimestamp &&
          favoriteItem.duration === selectedProposal.duration &&
          favoriteItem.departureCity === departureCity &&
          favoriteItem.kids?.length === selectedProposal.attendees?.children?.length &&
          favoriteItem.product?.id === selectedProposal.productId &&
          favoriteItem.reference !== selectedProposal.id
        );
      },
    );

    if (isShoppingFavorite && !isProposalFavorite) {
      showSavedCriteriaPopIn(false);
    }
  }

  function extendProposalPersistence(extend: boolean) {
    const { selectedProposalId } = router.query;
    startLoading();
    patchProposalHandler(selectedProposalId, context, extend)
      .then(() => {
        setShouldShowSaveCriteriaConfirmation(true);
        setPersistenceExtended(extend);
      })
      .catch((error) => {
        logger.error(error);
      })
      .finally(() => {
        endLoading();
      });
  }

  function showSavedCriteriaPopIn(popupDisplayed: boolean = true) {
    if (context.isUserLogged) {
      trackAddToWishlist();
      const { selectedProposalId } = router.query;
      const customerId = context.neolaneId;
      saveCustomerProposalsHandler(selectedProposalId, context, customerId)
        .then(() => {
          try {
            setSavedProposal(selectedProposal);
            setIsProposalFavorite(true);

            if (unlockedStepRef.current >= 4) {
              extendProposalPersistence(true);
            } else {
              setShouldShowSaveCriteriaConfirmation(popupDisplayed);
            }
          } catch (errors) {
            logger.error(errors);
          }
        })
        .catch((error) => {
          if (error && error.errors) {
            const {
              errors: [{ statusCode }],
            } = error;

            if (statusCode === 401) {
              setShouldShowSaveCriteriaLogin(popupDisplayed);
            }
          }
        });
    } else {
      setShouldShowSaveCriteriaLogin(true);
    }
  }

  function onClickFavorite(origin: 'header' | 'bottomBar') {
    if (!isProposalFavorite) {
      trackClickFavorite(origin, 'travel_plans');
      setClickOrigin(origin);
      showSavedCriteriaPopIn();
    }
  }

  function setFavorite(isFavorite: boolean) {
    setIsProposalFavorite(isFavorite);
  }

  const goToNextStep =
    (validationCallback?: () => Promise<any>) =>
    (params?: { queryParams: Record<string, string> }) => {
      const validationPromise = validationCallback ? validationCallback() : Promise.resolve();
      return validationPromise
        .then(() => {
          const pages = getFilteredPages();
          const currentStepFromRoute = getStepIndex(pages, router.query.bookingStep);
          const maxPagesIndex = pages.length - 1;

          if (currentStepFromRoute === maxPagesIndex) {
            return;
          }

          updateStep(
            Math.min(maxPagesIndex, currentStepFromRoute + 1),
            null,
            params?.queryParams || {},
          );
        })
        .catch(() => null);
    };

  function goToPreviousStep() {
    const stepIndex = getCurrentStepIndex();
    trackClickPreviousStep();

    if (stepIndex !== 0) {
      return updateStep(stepIndex - 1);
    }

    let previousUri;
    const { query: queryParams } = router;

    if (queryParams.fromSearches) {
      previousUri = new URI(urlPattern('accountSearches'))
        .query({
          ...omit(queryParams, [
            'bookingStep',
            'proposalIds',
            'selectedProposalId',
            'product',
            'coderesort',
            'fromSearches',
            'catId',
          ]),
        })
        .toString();
    } else {
      previousUri = new URI(get(resort, 'href'))
        .query({
          ...omit(queryParams, ['bookingStep', 'proposalIds', 'selectedProposalId', 'catId']),
        })
        .toString();
    }

    return router.push(urlUtils.normalizeUrl(previousUri).toString());
  }

  function openLayer(layerName: string) {
    const uri = new URI(router.asPath).addQuery({
      [SHOW_LAYER_QUERY_NAME]: layerName,
    });
    router.push(uri.toString());
  }

  function closeLayer(
    submitted: boolean | null | undefined,
    proposalId?: string | null | undefined,
  ) {
    if (submitted) {
      const nextStep =
        router.query.openLayer === 'AdditionalTransportsLayer'
          ? stepRef.current
          : stepRef.current + 1;
      updateStep(nextStep, typeof proposalId === 'string' ? proposalId : undefined);
    } else {
      const newUrl = new URI(router.asPath).removeQuery(SHOW_LAYER_QUERY_NAME);

      if (proposalId && typeof proposalId === 'string') {
        newUrl.setQuery('proposalIds', getAllProposalsIdWithAlternative(selectedProposal));
      }

      router.push(newUrl.toString());
    }
  }

  function hideSaveCriteriaConfirmationModal() {
    setShouldShowSaveCriteriaConfirmation(false);

    if (router.query.openSaveConfirmationModal) {
      const newUrl = new URI(router.asPath).removeQuery([
        'openSaveConfirmationModal',
        'newAccount',
      ]);
      router.replace(newUrl.toString());
    }
  }

  function hideSaveCriteriaLoginModal() {
    setShouldShowSaveCriteriaLogin(false);
  }

  function toggleShareContextModal() {
    if (!shouldShowShareContext) {
      trackClickShare();
    }

    setShouldShowShareContext((prev) => !prev);
    closeToast();
  }

  function getActions() {
    const {
      responsive: { isTablet },
    } = props;
    const pages = getFilteredPages();
    const isDisplaySaveFlipped = get(props, 'i18n.booking.saveMyCriteria.display');
    const isDisplayShareFlippedMobile = get(props, 'i18n.booking.common.share.displayMobile');
    const isDisplayShareFlippedDesktop = get(props, 'i18n.booking.common.share.displayDesktop');
    const shouldDisplaySave = isDisplaySaveFlipped && pages[step].name !== PAGES.BANKING;
    const shouldDisplayShare =
      ((isTablet && isDisplayShareFlippedDesktop) || (!isTablet && isDisplayShareFlippedMobile)) &&
      pages[step].name !== PAGES.BANKING;
    return [
      {
        id: persistenceExtended ? BE_UNSAVE_ACTION : BE_SAVE_ACTION,
        iconName: persistenceExtended || isProposalFavorite ? ICONS.STAR_FILLED : ICONS.STAR_HOLLOW,
        label: get(props, 'i18n.booking.common.save'),
        onClick: () => onClickFavorite('header'),
        display: shouldDisplaySave,
      },
      {
        id: BE_SHARE_ACTION,
        iconName: ICONS.SHARE,
        label: get(props, 'i18n.booking.common.share.label'),
        display: shouldDisplayShare,
        onClick: toggleShareContextModal,
      },
    ];
  }

  function updateStepsFromProps() {
    if (isEmpty(proposals) || !resort || !i18n.booking || !selectedProposal) {
      console.info('CM_AB: Skipping update - missing required data');
      return;
    }

    const isAbDataReady =
      abTastyData &&
      Object.keys(abTastyData).length > 0 &&
      Object.values(abTastyData).every((v) => v === '0' || v === '1');
    console.info('CM_AB: updateStepsFromProps called', {
      abTastyData,
      isAbDataReady,
      hasProposals: !isEmpty(proposals),
      hasResort: !!resort,
      hasI18n: !!i18n.booking,
      hasSelectedProposal: !!selectedProposal,
      willRender: !isEmpty(proposals) && !!resort && !!i18n.booking && !!selectedProposal,
    });

    const shouldHideActivitiesStepAbTest = computeShouldHideActivitiesStepAbTest(abTastyData);
    const isPartiallyPaid = attendeesPaymentStatus.some((s) => s.hasPaid);
    console.info('CM_AB: Activities step visibility:', {
      shouldHideActivitiesStepAbTest,
      abTestValue: abTastyData?.['ab-test-activities'],
    });
    const availableSteps = getFilteredBreadcrumbPages({
      selectedProposal,
      resort,
      i18n,
      isActivitiesViewFeatureFlipped: displayActivities,
      isOptionHoldOnlyFeatureFlipped: optionHoldOnly,
      isPartiallyPaid,
      shouldHideActivitiesStepAbTest,
    });
    const path = new URI(router.asPath).pathname();
    const stepIndex = availableSteps.findIndex((el) => path === el.path);
    const newStep = Math.max(0, stepIndex);
    const currentPreviousSelectedProposalId = previousSelectedProposalIdRef.current;
    const newUnlockedStep =
      !currentPreviousSelectedProposalId ||
      currentPreviousSelectedProposalId === selectedProposal.id
        ? Math.max(unlockedStepRef.current, stepIndex)
        : Math.min(
            unlockedStepRef.current,
            availableSteps.findIndex((el) => el.path.includes('participants')),
          );
    const stepChanged = stepRef.current !== newStep;
    const unlockedStepChanged = unlockedStepRef.current !== newUnlockedStep;
    const previousSelectedProposalIdChanged =
      previousSelectedProposalIdRef.current !== selectedProposal.id;

    if (!stepChanged && !unlockedStepChanged && !previousSelectedProposalIdChanged) {
      return;
    }

    setStep(newStep);
    setUnlockedStep(newUnlockedStep);
    previousSelectedProposalIdRef.current = selectedProposal.id;
  }

  // getDerivedStateFromProps equivalent: compute step and unlockedStep on every render
  if (!isEmpty(proposals) && resort && i18n.booking && selectedProposal) {
    const path = new URI(router.asPath).pathname();
    const shouldHideActivitiesStepAbTest = computeShouldHideActivitiesStepAbTest(abTastyData);
    const isPartiallyPaid = attendeesPaymentStatus.some((status) => status.hasPaid);
    const availableSteps = getFilteredBreadcrumbPages({
      selectedProposal,
      resort,
      i18n,
      isActivitiesViewFeatureFlipped: displayActivities,
      isOptionHoldOnlyFeatureFlipped: optionHoldOnly,
      isPartiallyPaid,
      shouldHideActivitiesStepAbTest,
    });
    const computedStepIndex = availableSteps.findIndex((el) => path === el.path);
    const newStep = Math.max(0, computedStepIndex);

    const newUnlockedStep =
      !previousSelectedProposalIdRef.current ||
      previousSelectedProposalIdRef.current === selectedProposal.id
        ? Math.max(unlockedStep, computedStepIndex)
        : Math.min(
            unlockedStep,
            availableSteps.findIndex((el) => el.path.includes('participants')),
          );

    if (step !== newStep) {
      setStep(newStep);
    }
    if (unlockedStep !== newUnlockedStep) {
      setUnlockedStep(newUnlockedStep);
    }
    if (previousSelectedProposalIdRef.current !== selectedProposal.id) {
      previousSelectedProposalIdRef.current = selectedProposal.id;
    }
  }

  // componentDidMount
  useEffect(() => {
    isMountedRef.current = true;

    try {
      handleWrongUrl(router);
    } catch {
      return;
    }

    resetTrackingStorageBE();
    handleNoData();
    const { query: queryParams } = router;
    const { proposalIds, selectedProposalId, openSaveConfirmationModal } = queryParams;

    if (isDataFetched && resort) {
      loadStreetViews({
        resort,
        withLoader: true,
      });
      checkIfFavoriFromShopping();
    }

    if (!isDataFetched) {
      startLoading();
      Promise.all([
        fetchProposalsById({
          proposalsId: proposalIds,
          appContext: context,
          shouldRequestPayment: isOnLastPage(),
        } as ProposalsByIdQueryParam & { appContext: AppContext }),
        getBookingEngineI18n(context),
        loadBookingStatus(queryParams?.bookingId),
      ])
        .then(async ([resp, , fetchedAttendeesPaymentStatus]) => {
          const { originalLocale } = selectedProposal;
          const { locale } = context;
          const hasSameLocale = locale === originalLocale;
          const fetchedProposals = resp?.proposals?.proposals;
          const responseResort = resp?.proposals?.resort;
          checkIfFavoriFromShopping();

          if (isEmpty(fetchedProposals) || !responseResort) {
            logger.error('API response with empty proposals');
            throw new ProposalNotFound(`No proposal found for this id: ${proposalIds.toString()}`);
          }

          const fetchedRefreshedProposal =
            isFeatureFlipped(context)('booking.refreshProposals') && hasSameLocale
              ? ((await fetchRefreshProposal(
                  selectedProposal.id,
                  undefined,
                  context,
                )) as unknown as { refreshProposal?: Proposal } | undefined)
              : undefined;
          setAttendeesPaymentStatus(fetchedAttendeesPaymentStatus);
          setRefreshedProposal(
            fetchedRefreshedProposal
              ? (fetchedRefreshedProposal?.refreshProposal as unknown as
                  | RefreshedProposal
                  | undefined)
              : undefined,
          );

          if (openSaveConfirmationModal && i18n.booking) {
            showSavedCriteriaPopIn();
          }

          loadStreetViews({
            resort: responseResort,
            withLoader: false,
          });
        })
        .catch((err) => {
          logger.error(err);
          endLoading();
          setHasInternalServerError(true);
          setIsDataFetched(true);

          if (isStrictlyProductionEnv()) {
            const { locale } = router.query;
            return router.replace({
              pathname: '/500',
              query: Boolean(locale) && {
                locale,
              },
            });
          }
        });
      const updatedUri = new URI(router.asPath).setQuery(
        appQueryParameters.booking.selectedProposalId,
        selectedProposalId || proposalIds?.[0],
      );
      router.replace(updatedUri.toString());
      selectProposal(selectedProposalId || proposalIds[0]);
      return;
    }

    updateStepsFromProps();
    Cookies.remove(SVRL_PMNTS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ABTasty event listener setup and cleanup
  useEffect(() => {
    if (process.env.NODE_ENV === 'test') {
      return;
    }

    function handleABTastyUpdate() {
      console.info('CM_AB: handleABTastyUpdate called');
      updateStepsFromProps();
      forceUpdate();
      console.info('CM_AB: handleABTastyUpdate completed');
    }

    document.addEventListener('abTastyDataUpdated', handleABTastyUpdate);

    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      window.forceBookingEngineUpdate = () => {
        console.info('CM_AB: forceUpdateFromABTasty called');
        updateStepsFromProps();
        forceUpdate();
        console.info('CM_AB: forceUpdateFromABTasty completed');
      };
    }

    return () => {
      document.removeEventListener('abTastyDataUpdated', handleABTastyUpdate);

      if (
        typeof window !== 'undefined' &&
        process.env.NODE_ENV === 'development' &&
        window.forceBookingEngineUpdate
      ) {
        delete window.forceBookingEngineUpdate;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // componentDidUpdate: handleNoData on every render
  useEffect(() => {
    if (!isMountedRef.current) {
      return;
    }
    handleNoData();
  });

  // componentDidUpdate: load street views when data is fetched but views are empty
  useEffect(() => {
    if (isDataFetched && isEmpty(streetViews)) {
      loadStreetViews({
        resort,
        withLoader: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataFetched, streetViews]);

  // componentDidUpdate: endLoading when isDataFetched transitions to true
  const prevIsDataFetchedRef = useRef(isDataFetched);
  useEffect(() => {
    if (!prevIsDataFetchedRef.current && isDataFetched) {
      endLoading();
    }
    prevIsDataFetchedRef.current = isDataFetched;
  }, [isDataFetched, endLoading]);

  // componentDidUpdate: handle save confirmation modal from query params
  useEffect(() => {
    const { openSaveConfirmationModal } = router.query;
    if (isDataFetched && openSaveConfirmationModal && i18n.booking) {
      showSavedCriteriaPopIn();
      router.replace(new URI(router.asPath).removeQuery(['openSaveConfirmationModal']).toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataFetched, router.query.openSaveConfirmationModal, i18n.booking]);

  // componentDidUpdate: detect when data becomes available
  useEffect(() => {
    if (!isDataFetched && !shouldFetchData()) {
      setIsDataFetched(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDataFetched, proposals, resort, i18n, router.query.proposalIds]);

  // componentDidUpdate: editCriteriaWarning handling
  useEffect(() => {
    if (typeof router.query.editCriteriaWarning !== 'undefined') {
      setUnlockedStep(0);
      const url = new URI(router.asPath);
      if (!url.toString().includes('accommodation')) {
        const newUrl = url.removeSearch('editCriteriaWarning');
        router.replace(newUrl.toString());
      }
    } else if (selectedProposal && resort && i18n.booking) {
      const currentStepIndex = getCurrentStepIndex();
      if (currentStepIndex > unlockedStep) {
        setUnlockedStep(currentStepIndex);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.asPath, selectedProposal?.id, resort, i18n.booking]);

  // componentDidUpdate: persistenceExtended sync from proposal
  const prevPersistenceExtendedRef = useRef(selectedProposal?.persistenceExtended);
  useEffect(() => {
    if (prevPersistenceExtendedRef.current !== selectedProposal?.persistenceExtended) {
      setPersistenceExtended(selectedProposal.persistenceExtended);
    }
    prevPersistenceExtendedRef.current = selectedProposal?.persistenceExtended;
  }, [selectedProposal?.persistenceExtended]);

  // componentDidUpdate: optionHoldOnly recalculation when households change
  const prevHouseholdsLengthRef = useRef(selectedProposal?.households?.length);
  useEffect(() => {
    if (prevHouseholdsLengthRef.current !== selectedProposal?.households?.length) {
      const newOptionHoldOnly =
        (isFeatureFlipped(context)('booking.optionHoldOnly') ||
          (selectedProposal?.households?.length > 1 &&
            isFeatureFlipped(context)('booking.multiHouseholdOptionOnly'))) &&
        selectedProposal?.price?.currency !== 'MXN';

      if (newOptionHoldOnly !== optionHoldOnly) {
        setOptionHoldOnly(newOptionHoldOnly);
      }
    }
    prevHouseholdsLengthRef.current = selectedProposal?.households?.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProposal?.households?.length]);

  // componentDidUpdate: reset favorite when price changes
  const prevPriceTotalRef = useRef(selectedProposal?.price?.total?.value);
  useEffect(() => {
    if (prevPriceTotalRef.current !== selectedProposal?.price?.total?.value) {
      setIsProposalFavorite(false);
      setIsNextStep(false);
    }
    prevPriceTotalRef.current = selectedProposal?.price?.total?.value;
  }, [selectedProposal?.price?.total?.value]);

  // componentDidUpdate: ABTasty changes detection
  const prevAbTastyRef = useRef(abTastyData?.['ab-test-activities']);
  const prevSelectedProposalIdRef = useRef(selectedProposal?.id);
  const prevDisplayActivitiesRef = useRef(displayActivities);
  const prevOptionHoldOnlyRef = useRef(optionHoldOnly);
  useEffect(() => {
    const abChanged = prevAbTastyRef.current !== abTastyData?.['ab-test-activities'];
    const proposalChanged = prevSelectedProposalIdRef.current !== selectedProposal?.id;
    const flagsChanged =
      prevDisplayActivitiesRef.current !== displayActivities ||
      prevOptionHoldOnlyRef.current !== optionHoldOnly;

    if (abChanged || proposalChanged || flagsChanged) {
      updateStepsFromProps();
    }

    prevAbTastyRef.current = abTastyData?.['ab-test-activities'];
    prevSelectedProposalIdRef.current = selectedProposal?.id;
    prevDisplayActivitiesRef.current = displayActivities;
    prevOptionHoldOnlyRef.current = optionHoldOnly;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    abTastyData?.['ab-test-activities'],
    selectedProposal?.id,
    displayActivities,
    optionHoldOnly,
  ]);

  // --- Render ---

  const {
    router: {
      query: { bookingStep: currentStep, editCriteriaWarning, newAccount },
    },
    responsive: { isAppMounted },
  } = props;

  if (hasInternalServerError) {
    return null;
  }

  if (i18n.booking && displayRefreshProposalError) {
    return (
      <SiteContainer isWide>
        <div className="min-h-screen bg-white">
          <SiteContainer>
            <QuickSearchUnavailability
              title={i18n.booking.warnings.refreshFailedTitle}
              description={i18n.booking.warnings.refreshFailedDescription}
            />
          </SiteContainer>
        </div>
      </SiteContainer>
    );
  }

  if (!isDataFetched || !i18n.booking || !isAppMounted || (isDataFetched && isEmpty(proposals))) {
    return <AsyncLoader className="min-h-screen" />;
  }

  const currentPage = getCurrentPage() ?? ({} as BookingPageData);
  const { name, actionGA, seoTitle, gridSize } = currentPage;
  const isTransportView = name === PAGES.TRANSPORT;
  const isTransferView = isTransportView && isEmpty(selectedProposal.transports);
  const isSkipTestVisible = isTransferView || name === PAGES.SERVICES || name === PAGES.ACTIVITIES;
  const alertMessageNewProposal = get(i18n, 'booking.editCriteria.alertMessageNewProposal');
  const activeStep = getCurrentStepIndex();
  const selectedProposalPrice = get(selectedProposal, 'price');
  return (
    <BookingEngineContext.Provider // TODO: cleanup !!
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        currentStep: currentPage,
      }}
    >
      <BookingAccessibleTitle title={seoTitle} />
      <div className="relative">
        <HeaderLite actions={getActions()} product={resort} proposal={selectedProposal} />
        <main role="main">
          <BookingBreadcrumb
            resort={resort}
            selectedProposal={selectedProposal}
            activeStep={activeStep > 0 ? activeStep : 0}
            actionGA={actionGA}
            backgroundImageSrc={resort.image}
            items={getFilteredPages().map((item) => ({
              iconName: item.icon,
              id: item.actionGA,
              label: item.title,
            }))}
            label={i18n?.booking?.header?.bookingSteps}
            lastActiveStep={unlockedStep}
            onClickStep={updateStep}
            i18n={i18n}
          />
          <div className={classnames('min-h-[60vh]', isTransportView ? 'py-10' : 'py-20')}>
            <SiteContainer
              size={
                (gridSize ? gridSize : 'small') as 'small' | 'mini' | 'medium' | 'large' | 'none'
              }
            >
              <div className="relative flex justify-between">
                {editCriteriaWarning && name === PAGES.ACCOMMODATION && alertMessageNewProposal && (
                  <div
                    className="mb-20 flex items-start justify-center gap-10 text-orange"
                    data-testid="BookingEngine-accommodationEditWarning"
                  >
                    <Icon className="shrink-0" name={ICONS.WARNING} width="1.5rem" />
                    {alertMessageNewProposal}
                  </div>
                )}
                {isSkipTestVisible && (
                  <button
                    className="ms-auto flex items-center justify-center font-bold text-black"
                    data-selector="skip-step"
                    onClick={() => {
                      trackClickSkipStep();
                      goToNextStep(undefined)();
                    }}
                    type="button"
                    data-testid="skip"
                  >
                    {i18n?.booking?.common?.skipThisStepLabel}
                    <Icon hasRtl name={ICONS.ARROW_RIGHT} width="1rem" />
                  </button>
                )}
              </div>
              <BookingLayout
                selectedProposal={selectedProposal}
                price={selectedProposalPrice}
                goToNextStep={goToNextStep}
                i18n={i18n}
                proposals={proposals}
                resort={resort}
                isOptionHoldOnly={optionHoldOnly}
                previousAction={goToPreviousStep}
                isOnLastPage={isOnLastPage()}
                isFavorite={isProposalFavorite}
                shouldOpenPanel={shouldOpenPanel}
                onClickFavorite={() => onClickFavorite('bottomBar')}
                isNextStep={isNextStep}
                currentPage={currentPage.name}
                closePanel={() => {
                  setShouldOpenPanel(false);
                }}
              >
                <BookingStep
                  actionGA={actionGA}
                  attendeesPaymentStatus={attendeesPaymentStatus}
                  closeLayer={closeLayer}
                  currentStep={currentStep}
                  goToNextStep={goToNextStep()}
                  i18n={i18n}
                  isFavorite={isProposalFavorite}
                  isOptionHoldOnly={optionHoldOnly}
                  openLayer={openLayer}
                  proposals={proposals}
                  resort={resort}
                  selectedProposal={selectedProposal}
                  streetViews={streetViews}
                />
              </BookingLayout>

              {shouldShowSaveCriteriaConfirmation ? (
                <SaveCriteriaConfirmation
                  persistenceExtended={persistenceExtended}
                  closeModal={hideSaveCriteriaConfirmationModal}
                  newAccount={newAccount}
                  i18n={i18n}
                />
              ) : null}
              {shouldShowSaveCriteriaLogin ? (
                <SaveCriteriaLogin
                  closeModal={hideSaveCriteriaLoginModal}
                  clickOrigin={clickOrigin}
                />
              ) : null}

              {shouldShowShareContext ? (
                <ShareContext
                  actionGA={actionGA}
                  closeToast={closeToast}
                  dataSelector="ShareContext"
                  displayToastOnContextShare={displayToastOnContextShare}
                  i18n={i18n}
                  onToggleModal={toggleShareContextModal}
                />
              ) : null}
              {!!refreshedProposal && (
                <RefreshProposalModal
                  resort={resort}
                  refreshedProposal={refreshedProposal}
                  selectedProposal={selectedProposal}
                />
              )}
            </SiteContainer>
          </div>
        </main>
      </div>
      {currentStep === BOOKING_STEPS.PARTICIPANTS.BE_RESPONSIVE && (
        <div className="relative m-auto max-w-site-medium border-t-1 border-grey px-20 py-60">
          <ParticipantsReassurance i18n={i18n} proposal={selectedProposal} resort={resort} />
        </div>
      )}
      <React.Fragment>
        <BookingFooter footer={i18n.footer} className="mt-100" />
        <div className="h-[128px]" />
      </React.Fragment>
      <TravelPlansComponent
        savedProposal={savedProposal}
        selectedProposal={selectedProposal}
        resort={resort}
        setFavorite={setFavorite}
      />
      <BlockMismatchProposalCountry selectedProposal={selectedProposal} resort={resort} />
    </BookingEngineContext.Provider>
  );
});

const BookingEngine = (props: Props) => {
  const {
    selectedProposal,
    resort,
    router: {
      query: { bookingStep: step, proposalIds },
    },
  } = props;
  const { trackEvent, trackClickEvent } = useTrackEvent();
  const shouldTrack = !!selectedProposal && !!resort;
  // in the case of a single proposal it comes as an item Otherwise as an Array ...
  let normalizedProposalIds: string[] = [];
  if (Array.isArray(proposalIds)) {
    normalizedProposalIds = proposalIds.filter((id): id is string => typeof id === 'string');
  } else if (typeof proposalIds === 'string') {
    normalizedProposalIds = [proposalIds];
  }
  const sortedProposalIds = [...normalizedProposalIds].sort((a, b) => a.localeCompare(b));
  const beginCheckoutCookie = sortedProposalIds.join('_') + '_co';
  useEffect(() => {
    const hasAlreadyTracked = Cookies.get(beginCheckoutCookie);

    if (hasAlreadyTracked || !selectedProposal || !resort) {
      return;
    }

    trackEvent(TRACK_EVENT_NAMES.BEGIN_CHECKOUT, {
      ecommerce: {
        items: [
          {
            item_id: resort.id,
            item_name: resort.name,
            price: selectedProposal.price.total.value,
          },
        ],
      },
    });
    trackEvent(TRACK_EVENT_NAMES.ADD_TO_CART, {
      ecommerce: {
        value: selectedProposal.price?.total?.value,
        currency: selectedProposal.price?.currency,
        items: [
          {
            item_id: resort.id,
            item_name: resort.name,
            item_category: resort.destination.country,
            item_category2: resort.comfort,
            price: selectedProposal.price.total.value,
            quantity: 1,
          },
        ],
      },
    });
    Cookies.set(beginCheckoutCookie, 'true', {
      expires: 1,
    });
  }, [sortedProposalIds, resort, selectedProposal]);
  const trackAddToWishlist = useCallback(() => {
    trackEvent(TRACK_EVENT_NAMES.ADD_TO_WISHLIST, {
      ecommerce: {
        items: [
          {
            item_id: selectedProposal.id,
            item_name: resort?.name,
            item_category: selectedProposal.accommodations?.map(
              (accommodation) => accommodation.roomType,
            )[0],
            price: selectedProposal.price?.total.value,
            item_category2: moment(selectedProposal.departureDate).format('L'),
            item_category3: selectedProposal.duration,
            item_category4: dateDiff(selectedProposal.departureDate, new Date()),
            item_category5: Boolean(selectedProposal.transports),
            coupon: selectedProposal.price?.promoCode?.offerId,
            discount: selectedProposal.price?.discounts?.reduce(
              (acc, currentValue) => acc + currentValue.amount.value,
              0,
            ),
          },
        ],
      },
    });
  }, [selectedProposal, resort]);
  const trackClickFavorite = useCallback((origin: 'header' | 'bottomBar', detailClick: string) => {
    trackClickEvent(
      origin === 'bottomBar' ? TRACK_EVENT_NAMES.CLICK_BOTTOM_BAR : TRACK_EVENT_NAMES.CLICK_HEADER,
      {
        detail_click: detailClick,
      },
    );
  }, []);
  const trackClickShare = useCallback(() => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_HEADER, {
      detail_click: 'share',
    });
  }, []);
  const trackClickPreviousStep = useCallback(() => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_BOTTOM_BAR, {
      detail_click: 'back_step',
    });
  }, []);
  const trackClickSkipStep = useCallback(() => {
    trackClickEvent(TRACK_EVENT_NAMES.CLICK_BREADCRUMB, {
      detail_click: 'skip_step',
    });
  }, []);
  const trackingContext = useMemo(() => {
    if (!shouldTrack) {
      return undefined;
    }

    const arrivalDate = moment(selectedProposal.arrivalDate);
    const daysBeforeDeparture = arrivalDate.diff(moment(), 'days');
    const transportDeparture = get(
      selectedProposal.transports,
      'inbound.travelSections[0].departure',
      {},
    ) as TravelSectionBound | { label?: string };
    const isEclusiveCollectionAccommodation = get(
      selectedProposal,
      'accommodations[0].exclusiveCollection',
    );
    return {
      [`base_price_${step}`]: selectedProposal.price?.total.value,
      reference_id: selectedProposal.id,
      days_before_departure: daysBeforeDeparture,
      departure_date_range: getDepartureDateRange(daysBeforeDeparture),
      departure_quarter: arrivalDate.quarter(),
      departure_semester: arrivalDate.quarter() > 2 ? 2 : 1,
      resort_name: resort.name,
      resort_code: resort.id,
      resort_country: resort.destination.country,
      resort_season_type: resort?.seasonName,
      resort_region: resort.destination.area,
      resort_exclusive_collection: getExclusiveCollectionFromAccomodation(
        resort.hasECSpace,
        isEclusiveCollectionAccommodation,
      ),
      resort_type: resort?.type?.toLowerCase() || 'no',
      resort_adult: selectedProposal.attendees.adults.length,
      resort_children: selectedProposal.attendees.children.length,
      resort_departure_city_name: transportDeparture.label || 'no_transport',
      resort_departure_date: moment(selectedProposal.arrivalDate).format('DD/MM/YYYY'),
      resort_checkout_date: selectedProposal?.departureDate
        ? moment(selectedProposal?.departureDate).format('DD/MM/YYYY')
        : moment(selectedProposal?.arrivalDate)
            .add(selectedProposal?.duration, 'days')
            .format('DD/MM/YYYY'),
      resort_night_number: selectedProposal.duration,
      resort_participants: selectedProposal.attendees.list.length,
      resort_transport: selectedProposal.transports ? 'with_transport' : 'without_transport',
      resort_year_season: moment(selectedProposal.departureDate || null, 'YYYYMMDD').format('YYYY'),
      room_type: selectedProposal.accommodations[0].comfortId?.split('_')?.reverse()?.[0],
    };
  }, [selectedProposal, resort, shouldTrack, step]);
  return (
    <BookingEngineTrackingContext.Provider value={trackingContext}>
      <BookingEngineComponent
        {...props}
        trackClickFavorite={trackClickFavorite}
        trackClickPreviousStep={trackClickPreviousStep}
        trackAddToWishlist={trackAddToWishlist}
        trackClickShare={trackClickShare}
        trackClickSkipStep={trackClickSkipStep}
        abTastyData={props.abTastyData}
      />
      {shouldTrack && (
        <TrackPage
          pageName={TRACK_PAGE_NAMES[`BE_${step.toUpperCase()}`]}
          payload={{ ...trackingContext }}
        />
      )}
    </BookingEngineTrackingContext.Provider>
  );
};

export { BookingEngineComponent };

const BookingEngineWithAbTastyData = (props) => {
  const { abTastyData } = useContext(ABTestContext);
  return <BookingEngine {...props} abTastyData={abTastyData} />;
};

export default responsiveness(withRouter(BookingEngineWithAbTastyData));
